import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Image, Text, XStack, YStack } from 'tamagui';
import { tokens } from 'tamagui-config';

import { humidityAlertIcon } from '../../../assets';
import { GlowBackground } from '../../../components/common/GlowBackground';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';
import {
  HumidityAlertState,
  useHumidityAlertContext,
} from '../../../contexts/HumidityAlertContext';

export const HumidityAlertCard = () => {
  const { t } = useTranslation();
  const {
    alertState,
    alertMessage,
    hasActiveHumidityAlerts,
    onRequestService,
    onClearAlert,
    loading,
  } = useHumidityAlertContext();

  // Don't render if no active humidity alerts
  if (!hasActiveHumidityAlerts || alertState === HumidityAlertState.NoAlert) {
    return null;
  }

  // Determine icon and color based on alert state
  let icon, color, message;
  switch (alertState) {
    case HumidityAlertState.ServiceRequested:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.deltaBlue} />;
      color = tokens.color.deltaBlue;
      message = t('humidity.serviceRequested');
      break;
    case HumidityAlertState.ServiceScheduled:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.successGreen} />;
      color = tokens.color.successGreen;
      message = t('humidity.serviceScheduled');
      break;
    case HumidityAlertState.InProgress:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.deltaBlue} />;
      color = tokens.color.deltaBlue;
      message = t('humidity.serviceInProgress');
      break;
    case HumidityAlertState.HumidityCautionAlert:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.warning} />;
      color = tokens.color.warning;
      message = alertMessage;
      break;
    case HumidityAlertState.HumidityHighAlert:
    case HumidityAlertState.HumidityExtremeAlert:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.warningRed} />;
      color = tokens.color.warningRed;
      message = alertMessage;
      break;
    default:
      icon = <Image src={humidityAlertIcon} width={80} height={80} tintColor={tokens.color.warning} />;
      color = tokens.color.warning;
      message =
        alertMessage ||
        t('humidity.cautionaryHumidityDetectedIn', { location: t('humidity.unknownLocation') });
      break;
  }

  // Determine button visibility based on alert state
  const showRequestServiceButton =
    alertState === HumidityAlertState.HumidityCautionAlert ||
    alertState === HumidityAlertState.HumidityHighAlert ||
    alertState === HumidityAlertState.HumidityExtremeAlert;

  return (
    <YStack flex={1} gap="$4">
      <LoadingOverlay isVisible={loading} />

      {!loading && (
        <>
          <GlowBackground color={color} />
          <YStack flex={1} justifyContent="center">
            <XStack gap="$4" alignItems="center">
              <YStack width={80} height={80}>
                {icon}
              </YStack>
              <Text color={color} fontSize="$9.5">
                {message}
              </Text>
            </XStack>
          </YStack>

          {/* Show buttons based on alert state */}
          <XStack gap="$4">
            {showRequestServiceButton && (
              <Button
                flex={1}
                height={72}
                backgroundColor="$deltaBlue"
                borderRadius="$10"
                onPress={onRequestService}
                disabled={loading}>
                <Text color="$white12" fontSize="$8.5">
                  {t('humidity.requestService')}
                </Text>
              </Button>
            )}
            <Button
              flex={1}
              height={72}
              backgroundColor="$transparent"
              borderRadius="$10"
              borderWidth={2}
              borderColor="$deltaBlue"
              onPress={onClearAlert}
              disabled={loading}>
              <Text color="$white12" fontSize="$8.5">
                {t('humidity.clearAlert')}
              </Text>
            </Button>
          </XStack>
        </>
      )}
    </YStack>
  );
};
